import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../i18n";

const FLAGS = { en: "🇬🇧", fr: "🇫🇷", es: "🇪🇸", ar: "🇸🇦" };

// Accessible language dropdown. The chosen language is persisted by i18next
// (localStorage) and the document direction is updated automatically.
export default function LanguageSwitcher({ variant = "light" }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = (i18n.language || "en").split("-")[0];
  const active = SUPPORTED_LANGUAGES.includes(current) ? current : "en";

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const change = (lng) => {
    i18n.changeLanguage(lng);
    setOpen(false);
  };

  const btnCls =
    variant === "dark"
      ? "text-white/90 hover:text-white border-white/30"
      : "text-gray-700 hover:text-purple-600 border-gray-200";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.choose")}
        className={`flex items-center gap-1.5 text-sm font-medium border rounded-full px-3 py-1.5 transition ${btnCls}`}
      >
        <span aria-hidden="true">{FLAGS[active]}</span>
        <span className="uppercase">{active}</span>
        <svg className="w-3.5 h-3.5 opacity-70" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute end-0 mt-2 w-40 bg-white rounded-xl shadow-lg ring-1 ring-black/5 py-1 z-50"
        >
          {SUPPORTED_LANGUAGES.map((lng) => (
            <li key={lng}>
              <button
                type="button"
                role="option"
                aria-selected={active === lng}
                onClick={() => change(lng)}
                className={`w-full text-start flex items-center gap-2 px-3 py-2 text-sm hover:bg-purple-50 ${
                  active === lng ? "text-purple-600 font-semibold" : "text-gray-700"
                }`}
              >
                <span aria-hidden="true">{FLAGS[lng]}</span>
                {t(`lang.${lng}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
